import { IDENTITY_TIMEBASE, type AudioTimebase } from "../app/audio-timebase";
import type { ScheduleTicker } from "../app/schedule-ticker";
import { Transport } from "./transport";

/** Plage de bouclage, en ticks. `endTick` est exclusif. */
export type TransportLoop = {
  readonly startTick: number;
  readonly endTick: number;
};

/**
 * Les bornes du morceau courant, quand quelque chose existe au-delà.
 *
 * Posées par la séquence de morceaux (`SongSequence`) et par personne
 * d'autre : un studio qui ne tient qu'un morceau n'en a pas, et la tête s'y
 * comporte comme elle l'a toujours fait — elle continue dans le vide passé la
 * dernière note, et un déplacement avant le début s'arrête à zéro.
 */
export type TransportBound = {
  /** Un morceau précède celui-ci : un tick négatif est une destination. */
  readonly before: boolean;
  /** La fin du morceau, exclusive, quand un morceau la suit ; `null` sinon. */
  readonly endTick: number | null;
};

type LocalTransportEvent =
  | { type: 'seek'; tick: number }
  | { type: 'play'; tick: number }
  | { type: 'detach'; tick: number }
  /**
   * The playhead reached the end of the loop and was sent back to its start,
   * where it now sits **paused**. Restarting is deliberately left to the
   * listener: the Studio answers with a count-in, which is the pulse the
   * player needs to come back in. The transport owns the clock, not the
   * click track.
   */
  | { type: 'loop'; tick: number }
  /**
   * La tête est sortie du morceau courant : elle a atteint sa fin en lecture
   * (`tick` vaut alors exactement `endTick`), ou un déplacement l'a envoyée
   * au-delà, ou avant zéro (`tick` est alors la destination demandée, telle
   * quelle). Le transport n'a **rien fait** : ni avancé, ni déplacé la tête.
   * C'est à qui a posé la borne de la reposer dans le bon morceau — voir
   * `SongSequence`, qui répond toujours.
   */
  | { type: 'bound'; tick: number };

/**
 * La vue locale sur le temps de la session.
 *
 * # Décrocher, ce n'est pas mettre en pause
 *
 * La pause appartient à la session : quand elle s'arrête, elle s'arrête pour
 * tout le monde. Ce qui se passe ici est autre chose — je remonte à la molette,
 * je ralentis, je fige ma vue pour regarder un passage pendant que la séance
 * continue chez les autres. C'est un **décrochage**, d'où `detach()`, et
 * `followShared()` en est la sortie.
 *
 * # Deux échelles de temps
 *
 * Les positions viennent du transport, sur l'horloge de position. Les fenêtres
 * d'ordonnancement viennent du ticker, sur l'horloge du contexte audio. Ce
 * n'est pas la même échelle depuis qu'elles ont été séparées, et c'est
 * `AudioTimebase` qui passe de l'une à l'autre — aux deux seuls endroits où
 * elles se croisent, `getTimeFromTick` et `tickAt`.
 *
 * # Le délai de restitution
 *
 * En séance, une note reçue d'un partenaire porte le tick auquel il l'a
 * *jouée*. Le temps qu'elle arrive, ce tick est passé : programmée « au plus
 * tôt », elle sonne au rythme des arrivées, et la gigue du réseau tombe
 * directement dans ce qu'on entend. La grille partagée ne servirait alors qu'à
 * dessiner.
 *
 * Le remède est de faire courir la tête de lecture `D` **derrière** la
 * position de séance (`playoutDelaySeconds`). Tout ce qui se dessine et tout
 * ce qui sonne — mes notes, celles des partenaires, le morceau, le métronome
 * — est en retard de `D` sur le présent vrai, et une note de partenaire est
 * encore devant la tête quand elle arrive, tant que `D` couvre le trajet et
 * sa gigue. Elle se programme alors exactement à son tick.
 *
 * La capture, elle, se fait au **présent vrai** (`sourceTick`), `D` devant la
 * tête : le tick reste une vérité — « quand ça a été joué » — et c'est chez
 * chaque récepteur que le tampon se prend. Un participant qui ne le prend pas
 * n'entend pas juste, mais ne casse rien pour les autres. L'autre forme — la
 * capture qui avance son tick de `D` — était équivalente sur le papier, mais
 * un tick avancé ment sur l'instant du geste, et c'est l'émetteur qui décide
 * pour tout le monde du tampon.
 *
 * Mes propres notes sonnent donc `D` plus tard aussi. C'est voulu : si je
 * m'entendais à zéro et mon partenaire à `D`, je serais en avance sur lui dans
 * ma tête, je ralentirais pour l'attendre, et le tempo descendrait.
 *
 * `D` exactement, et non `D` plus le tampon de sortie de ma carte son : ce
 * qu'on programme vise l'oreille, pas la trame rendue. Voir
 * `getTimeFromTick`.
 *
 * **Ce délai n'est pas un décrochage.** Il vit dans son propre champ et
 * `isFollowingShared` l'ignore : le passer par `deltaTime` rendrait ce
 * getter faux, et `Scheduler.handleLiveEvent` refuse de faire sonner une lane
 * de partenaire quand il l'est — plus aucune note de pair ne sonnerait.
 */
export class LocalTransport {
  private deltaTime = 0;
  private playbackRate = 1;
  private detachedAt?: number = 0;

  /**
   * De combien la tête de lecture court derrière la position de séance, en
   * secondes. Zéro hors séance. Voir « Le délai de restitution » ci-dessus.
   */
  private playoutDelaySeconds = 0;

  /** Vrai dès que la vue a suivi la session une première fois. Voir `play()`. */
  private attachedOnce = false;
  private loop: TransportLoop | null = null;
  /** Les bornes du morceau courant, `null` tant qu'un seul morceau est chargé. Voir `setBound`. */
  private bound: TransportBound | null = null;
  private readonly timebase: AudioTimebase;

  private listeners = new Set<(fromTick: number, scheduleUntilTick: number) => void>();
  private eventListeners = new Set<(event: LocalTransportEvent) => void>();
  private boundOnSchedule: (audioTime: number, scheduleUntil: number) => void;
  private readonly ticker?: ScheduleTicker;

  /**
   * Sans ticker, la vue sait tout dire du temps mais n'ordonnance rien, et les
   * deux échelles sont confondues. C'est le cas des tests, et de tout ce qui
   * lit des positions sans produire de son.
   */
  constructor(public readonly transport: Transport, ticker?: ScheduleTicker) {
    this.boundOnSchedule = (audioTime, scheduleUntil) => this.onSchedule(audioTime, scheduleUntil);
    this.ticker = ticker;
    this.timebase = ticker?.timebase ?? IDENTITY_TIMEBASE;
    ticker?.register(this.boundOnSchedule);
  }

  seek(time: number) {
    time = Math.min(time, this.transport.elapsedTime());

    // Use the effective reference point: when detached, anchor to detachedAt
    // so getDeltaTime() compensates correctly regardless of call order
    // with play().
    const ref = this.detachedAt ?? this.transport.elapsedTime();
    this.deltaTime = time + this.playoutDelaySeconds - ref;
    this.emit({ type: 'seek', tick: this.currentTick() });
  }

  /**
   * Seek to an absolute tick position.
   * Unlike seek(), this allows seeking forward beyond the current playback position
   * (useful for scrolling through a song with the mouse wheel).
   */
  seekToTick(tick: number) {
    // Hors du morceau courant, ce n'est plus un déplacement : c'est un
    // changement de morceau, et la tête ne bouge pas d'ici. Voir `setBound`.
    if (this.leavesBound(tick)) {
      this.emit({ type: 'bound', tick });

      return;
    }

    tick = Math.max(0, tick);
    const targetSeconds = this.transport.timeline.tickToSeconds(tick) / this.playbackRate;

    // Use the effective reference point: when detached, anchor to detachedAt
    // so getDeltaTime() compensates correctly regardless of call order
    // with play().
    const ref = this.detachedAt ?? this.transport.elapsedTime();
    this.deltaTime = targetSeconds + this.playoutDelaySeconds - ref;
    this.emit({ type: 'seek', tick: this.currentTick() });
  }

  /**
   * Set (or clear) the loop range.
   *
   * Held here rather than above the transport because looping is a decision
   * about time, and this is the only object that owns the clock. An empty or
   * inverted range clears the loop instead of wedging playback.
   */
  setLoop(loop: TransportLoop | null) {
    this.loop = loop !== null && loop.endTick > loop.startTick ? loop : null;
  }

  getLoop(): TransportLoop | null {
    return this.loop;
  }

  /**
   * Pose les bornes du morceau courant, ou les retire avec `null`.
   *
   * # Pourquoi ici, et pas au-dessus
   *
   * Même raison que la boucle : sortir du morceau est une décision sur le
   * temps, et cet objet est le seul qui tienne l'horloge. Une séquence de
   * morceaux qui aurait guetté la fin de l'extérieur — sur les fenêtres
   * d'ordonnancement, ou sur les événements `seek` — aurait changé de
   * morceau **pendant** la diffusion de la fenêtre ou de l'événement qui l'a
   * prévenue. Les ordonnanceurs du morceau suivant, construits pendant cette
   * diffusion, auraient alors reçu la fin de cette même fenêtre — en ticks de
   * l'ancien morceau — et confié au synthé, d'un coup, tout ce qui précède ce
   * tick dans le nouveau. Ou bien, sur un `seek` sorti du morceau, reçu après
   * coup l'ancien tick et sauté toutes les notes entre la vraie position et
   * lui.
   *
   * Tenue ici, la sortie est constatée **avant** qu'une fenêtre ou un `seek`
   * soit diffusé : rien n'est distribué, l'événement `bound` part seul, et qui
   * y répond place la tête dans le bon morceau par un `seekToTick` ordinaire —
   * le seul que les ordonnanceurs verront.
   *
   * # Ce que ça ne change pas
   *
   * Sans borne — un seul morceau chargé, le cas de tout le studio jusqu'ici —,
   * rien n'est vérifié au-delà d'un test de nullité, et la tête fait ce qu'elle
   * a toujours fait : elle continue après la dernière note, et un déplacement
   * avant zéro s'arrête à zéro.
   */
  setBound(bound: TransportBound | null) {
    this.bound = bound;
  }

  getBound(): TransportBound | null {
    return this.bound;
  }

  /** Cette destination est-elle hors du morceau courant ? Jamais sans borne. */
  private leavesBound(tick: number): boolean {
    const bound = this.bound;

    if (bound === null) {
      return false;
    }

    if (tick < 0) {
      return bound.before;
    }

    return bound.endTick !== null && tick >= bound.endTick;
  }

  getPlaybackRate(): number {
    return this.playbackRate;
  }

  /**
   * Pose la vitesse de la vue locale, sans déplacer la tête de lecture.
   *
   * La vitesse presse le temps : `tickIn` et `tickAt` multiplient la position
   * *avant* de la convertir en ticks. L'écart est donc recalculé ici pour que
   * la position rende le tick courant à la nouvelle vitesse — exactement,
   * quelle que soit la carte de tempo, puisque les deux sens traversent la
   * même conversion.
   *
   * C'est ce qui manquait : la vitesse s'appliquait après la conversion, et un
   * coup de curseur sur un morceau dont le tempo change déplaçait le morceau
   * d'une noire ou plus, vers l'avant ou vers l'arrière selon l'endroit.
   */
  setPlaybackRate(rate: number) {
    const tick = this.currentTick();
    this.playbackRate = rate;
    // Use the effective reference point (same as seekToTick) so a detached
    // view is handled correctly and the tick position is preserved.
    const ref = this.detachedAt ?? this.transport.elapsedTime();
    this.deltaTime = this.transport.timeline.tickToSeconds(tick) / rate + this.playoutDelaySeconds - ref;
  }

  /**
   * Pose le délai de restitution, en secondes. Voir la note de tête.
   *
   * # Ce que ça déplace
   *
   * Un seek, `seekToTick` et `setPlaybackRate` placent la **tête de lecture**,
   * pas la position source : c'est elle qu'on voit et qu'on entend, et un
   * déplacement qui tomberait `D` à côté serait une panne. Ils ajoutent donc
   * `D` à l'écart, et le délai n'a pas besoin d'être nul pour qu'ils tombent
   * juste.
   *
   * # Pourquoi aucun événement
   *
   * Changer `D` fait sauter la tête de la différence. Émettre un `seek` ferait
   * relâcher puis reprogrammer les notes du morceau déjà confiées au synthé
   * entre l'ancienne et la nouvelle tête — on les entendrait deux fois. Sans
   * événement, elles sonnent à l'ancien retard et les suivantes au nouveau :
   * un accroc unique, de la taille du pas, à chaque marche du budget. C'est le
   * moindre mal, et le budget ne bouge qu'à l'arrivée d'une liaison.
   */
  setPlayoutDelay(seconds: number) {
    this.playoutDelaySeconds = Math.max(0, seconds);
  }

  /** Le délai de restitution en vigueur, en secondes. */
  getPlayoutDelay(): number {
    return this.playoutDelaySeconds;
  }

  /**
   * Décroche la vue locale : elle se fige là où elle est, la session continue.
   *
   * S'appelait `pause()`, ce qui était juste tant qu'il n'y avait qu'un joueur
   * — et faux dès qu'il y en a deux : arrêter de regarder n'arrête pas la
   * séance. La pause, elle, est passée à la session.
   */
  detach() {
    this.detachedAt = this.transport.elapsedTime();
    this.emit({ type: 'detach', tick: this.currentTick() });
  }

  /** Vrai quand la vue locale est figée. */
  get isDetached() {
    return this.detachedAt !== undefined;
  }

  play() {
    if (this.detachedAt === undefined) {
      return;
    }

    // Une vue qui n'a encore jamais suivi la session n'a rien à préserver :
    // elle naît figée à la position zéro, avant qu'aucune origine ne soit
    // posée sur le transport. La dégeler « où elle en était » la laisserait
    // en retard de ce qui s'est écoulé entre la pose de l'origine et ce
    // premier `play()` — une poignée de microsecondes, invisible sur le roll,
    // mais assez pour que `isFollowingShared` réponde faux à jamais, et donc
    // pour qu'une note de partenaire ne sonne plus tout de suite.
    if (!this.attachedOnce && this.deltaTime === 0 && this.playbackRate === 1) {
      this.followShared();

      return;
    }

    this.attachedOnce = true;
    this.deltaTime = this.getDeltaTime();
    this.detachedAt = undefined;
    this.emit({ type: 'play', tick: this.currentTick() });
  }

  /**
   * Recolle la vue locale sur le transport partagé.
   *
   * En séance, le transport partagé avance chez tout le monde en même temps —
   * c'est lui qui donne son sens au tick d'une note reçue. La vue locale, elle,
   * peut s'en écarter : on met en pause pour regarder un passage, on remonte à
   * la molette. Rien n'était prévu pour revenir, et le partenaire semblait
   * alors jouer dans le futur.
   *
   * Les trois termes qui font l'écart repartent donc à leur valeur neutre. On
   * ne « cherche » pas la position partagée, on cesse d'en dévier : c'est ce
   * qui garantit qu'on retombe exactement dessus, et pas à côté d'un tick.
   */
  followShared() {
    this.attachedOnce = true;
    this.deltaTime = 0;
    this.detachedAt = undefined;
    this.playbackRate = 1;
    this.emit({ type: 'play', tick: this.currentTick() });
  }

  /**
   * Vrai quand la vue locale suit le transport partagé.
   *
   * Le délai de restitution n'y entre pas, à dessein : reculer pour entendre
   * juste n'est pas décrocher du direct. `Scheduler.handleLiveEvent` s'en sert
   * pour décider si une note de partenaire sonne tout de suite — le compter
   * ici rendrait toute la séance muette dès que le budget serait posé.
   */
  get isFollowingShared() {
    return this.deltaTime === 0 && this.detachedAt === undefined && this.playbackRate === 1;
  }

  /**
   * Le tick sous la tête de lecture : ce qui se dessine et ce qui sonne.
   *
   * `at` — un instant sur l'horloge de position — lit la tête telle qu'elle
   * était à cet instant plutôt que maintenant. Voir `tickIn`.
   */
  currentTick(at?: number) {
    const tick = this.tickIn(0, at);

    // Figée, la tête est posée sur une position du morceau, et les notes y
    // commencent et y finissent sur des ticks entiers. L'aller-retour tick →
    // secondes → tick d'un `seekToTick(960)` rendait 959,9999999 : la note qui
    // commence là passait pour pas encore atteinte. En lecture la tête reste
    // continue — arrondie, elle avancerait par saccades.
    return this.detachedAt !== undefined ? Math.round(tick) : tick;
  }

  /**
   * Le tick du présent vrai de la séance : `D` devant la tête de lecture, et
   * indifférent à ce que la vue locale fait.
   *
   * Ce n'est **plus** le tick d'un geste — voir `captureTick`. Il sert à ce qui
   * doit se lire au présent de la séance quelle que soit la vue : une note
   * tenue par un partenaire s'étire jusqu'à lui sur le roll, puisque le
   * partenaire la tient *maintenant*.
   *
   * Quand la vue suit le partagé, c'est exactement le tick courant du transport
   * de séance.
   */
  sourceTick(at?: number) {
    return this.transport.timeline.secondsToTick(this.elapsedAt(at)) ?? 0;
  }

  /**
   * Le tick qu'un geste joué maintenant doit porter : la tête de lecture,
   * plus le délai de restitution.
   *
   * # À la tête, pas au présent de la séance
   *
   * On joue sur ce qu'on voit et ce qu'on entend, et c'est la tête. Remonter la
   * molette de dix secondes puis jouer pose la note dix secondes plus tôt,
   * exactement comme hors séance ; une vue figée pose la note sous la tête
   * figée. Une version du 2026-09-13 captait au présent de la séance quelle que
   * soit la vue, pour une raison qui n'existe plus : le trajet se mesurait alors
   * sur la grille, et une note datée dans le passé passait pour dix secondes de
   * réseau. Il se mesure désormais sur l'horloge du service (`lib/live/transit.ts`),
   * et rien de la grille n'y entre.
   *
   * # Plus le délai
   *
   * Quand la vue suit le direct, la tête court `D` derrière la séance, et un
   * tick capté sous elle serait déjà passé chez le partenaire à l'arrivée.
   * `D` devant la tête, c'est le présent de la séance : le tick de toujours.
   * Le leader n'a pas de cas particulier, son délai vaut zéro.
   *
   * `at` date le geste à l'instant où le navigateur l'a reçu, voir `tickIn`.
   */
  captureTick(at?: number) {
    return this.tickIn(this.playoutDelaySeconds, at);
  }

  /**
   * Le tick que la tête de lecture atteindra dans `seconds`.
   *
   * Passe par la conversion complète plutôt que par une règle de trois : la
   * carte de tempo rend la relation tick ↔ seconde dépendante de l'endroit du
   * morceau, et un décalage constant en secondes n'est pas un décalage constant
   * en ticks.
   *
   * `at` déplace le point de départ : la tête telle qu'elle était à cet
   * instant de l'horloge de position, et non maintenant. C'est ce qui permet
   * de dater un geste à l'instant où le navigateur l'a reçu plutôt qu'à celui
   * où le fil principal a pu s'en occuper — une image de retard quand le roll
   * rend, parfois plus. Une vue décrochée est figée : son tick ne dépend alors
   * d'aucun instant, et `at` n'y change rien.
   */
  tickIn(seconds: number, at?: number) {
    // Figée, la vue est à `detachedAt + deltaTime`, et à rien d'autre. Écrite
    // `elapsed + (deltaTime - (elapsed - detachedAt))`, elle annulait l'horloge
    // en virgule flottante, avec un reste différent à chaque image : une vue
    // arrêtée pile sur une attaque lisait 959,9999999 puis 960,0000001, et
    // toutes les notes qui commencent ou finissent là clignotaient sur le roll.
    const base =
      this.detachedAt !== undefined
        ? this.detachedAt + this.deltaTime
        : this.elapsedAt(at) + this.getDeltaTime(at);
    const position = base - this.playoutDelaySeconds + seconds;

    // La vitesse presse le **temps**, pas les ticks : elle multiplie la
    // position avant la conversion. Appliquée après — `secondsToTick(position)
    // * rate` —, elle traversait la carte de tempo, qui n'est pas linéaire dès
    // qu'un changement de tempo est écrit : un ralenti déplaçait alors la tête
    // de lecture au lieu de la laisser où elle était. Voir `setPlaybackRate`.
    return this.transport.timeline.secondsToTick(position * this.playbackRate) ?? 0;
  }

  /** La position de séance, maintenant ou à l'instant `at` de l'horloge. */
  private elapsedAt(at?: number) {
    return at === undefined ? this.transport.elapsedTime() : this.transport.elapsedTimeAt(at);
  }

  /**
   * L'heure, sur l'horloge audio, à laquelle programmer pour que ce tick
   * **s'entende** à sa place.
   *
   * La position se calcule d'abord — elle ne dépend que du transport — puis
   * `AudioTimebase` la porte sur l'échelle du contexte, la seule où
   * programmer une note ait un sens.
   *
   * Le délai de restitution s'ajoute ici : un tick tombe `D` plus tard sur
   * l'horloge audio, pour le morceau et le métronome comme pour un partenaire.
   *
   * # Pourquoi la sortie audio se retranche
   *
   * Programmer à l'instant de la grille fait sonner la note à cet instant, et
   * l'*entendre* un tampon de sortie plus tard — une vingtaine de
   * millisecondes sur la sortie intégrée d'un Mac, trois sur une interface.
   * Deux machines qui posaient la même note au même instant de rendu, à un
   * demi-millième près, l'entendaient donc à douze millisecondes d'écart, et
   * aucune horloge partagée ne pouvait le voir : la grille s'arrêtait au rendu.
   *
   * On vise donc l'oreille. Ce qui sonne tombe sur la grille chez tout le
   * monde, quelle que soit la carte son — et chacun s'entend soi-même avec son
   * délai de restitution *exactement*, au lieu de ce délai plus son tampon.
   *
   * Le prix est que le tampon disponible pour le réseau se réduit d'autant :
   * il faut désormais `D ≥ sortie audio + trajet` pour qu'une note de
   * partenaire tombe juste, là où `D ≥ trajet` suffisait à la faire sonner
   * (ailleurs). Le service en tient compte — il ajoute la sortie annoncée au
   * délai qu'il publie.
   *
   * L'invariant devient donc : `getTimeFromTick(currentTick())` vaut
   * maintenant **moins** le tampon de sortie, c'est-à-dire l'instant de rendu
   * dont l'oreille tombe sur maintenant.
   */
  getTimeFromTick(tick: number) {
    const position =
      (this.transport.timeline.tickToSeconds(tick) / this.playbackRate)
      + this.transport.getOrigin()
      - this.getDeltaTime()
      + this.playoutDelaySeconds;

    return this.timebase.toAudioTime(position) - this.timebase.outputLatencySeconds;
  }

  /**
   * L'instant audio correspondant à « maintenant, plus `delaySeconds` ».
   *
   * Passe par le raccord plutôt que par `ctx.currentTime` : la position vit sur
   * une horloge continue, l'ordonnancement sur celle de la carte son, et les
   * deux dérivent de quelques ppm. Additionner un retard à la mauvaise ne se
   * verrait pas tout de suite, mais glisserait avec le temps.
   */
  audioTimeIn(delaySeconds: number): number {
    return this.timebase.toAudioTime(this.transport.clock.currentTime + delaySeconds);
  }

  register(callback: (fromTick: number, scheduleUntilTick: number) => void): () => void {
    this.listeners.add(callback);

    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * La fenêtre d'ordonnancement, en ticks, pour ce tour de ticker.
   *
   * Elle passe par `tickAt`, donc elle recule de `D` avec le reste : les notes
   * d'un morceau lu en séance sont confiées au synthé `D` plus tard, comme
   * tout ce qui sonne. C'est voulu — la tête de lecture est l'instant sonore,
   * et rien ne doit sonner devant elle.
   */
  onSchedule(audioTime: number, scheduleUntil: number) {
    // Ni pendant que la vue est décrochée — les `Scheduler` rejoueraient les
    // notes sous la tête de lecture toutes les 25 ms —, ni pendant que la
    // session est en pause : les fenêtres, elles, viennent de l'horloge audio,
    // qui continue d'avancer sans rien savoir de la pause.
    if (this.detachedAt !== undefined || this.transport.isPaused) {
      return;
    }

    const loop = this.loop;

    // Wrap: rewind, stop, and hand over. The Schedulers reindex on the `seek`
    // event and the `detach` releases the sounding notes; whoever listens for
    // `loop` decides when the music comes back.
    if (loop !== null && this.tickAt(audioTime) >= loop.endTick) {
      this.seekToTick(loop.startTick);
      this.detach();
      this.emit({ type: 'loop', tick: loop.startTick });

      return;
    }

    const fromTick = this.tickAt(audioTime);

    // La fin du morceau, atteinte en lecture : rien n'est distribué pour cette
    // fenêtre, et c'est la borne exacte qui est annoncée — pas le dépassement,
    // qui peut valoir un tour de ticker. Qui répond repose la tête au début du
    // morceau suivant, et la fenêtre d'après sera la sienne. Voir `setBound`.
    const bound = this.bound;

    if (bound !== null && bound.endTick !== null && fromTick >= bound.endTick) {
      this.emit({ type: 'bound', tick: bound.endTick });

      return;
    }

    let scheduleUntilTick = this.tickAt(scheduleUntil);

    // Never schedule past the loop end. The window looks ahead of the
    // playhead, so without this the notes sitting after the end would already
    // be handed to the synth with a future date — and `stopScheduledNotes()`
    // does not reach them, since they have not started sounding yet. They
    // would be heard as ghosts after the wrap.
    if (loop !== null) {
      scheduleUntilTick = Math.min(scheduleUntilTick, loop.endTick);
    }

    this.listeners.forEach(listener => listener(Math.floor(fromTick), Math.ceil(scheduleUntilTick)));
  }

  /**
   * Tick at a given audio-clock time, under the current offset and rate.
   *
   * Le miroir exact de `getTimeFromTick` : le délai de restitution s'y
   * retranche, et le tampon de sortie s'y **rajoute**. Les deux doivent rester
   * réciproques — c'est ce couple qui borne la fenêtre d'ordonnancement, et un
   * miroir de travers ferait chercher les notes ailleurs qu'où elles sont
   * programmées.
   *
   * Conséquence voulue : la fenêtre regarde un tampon de sortie plus loin en
   * ticks, puisque c'est d'autant plus tôt qu'il faut confier les notes au
   * synthé.
   */
  private tickAt(audioTime: number): number {
    const seconds =
      this.timebase.toPositionTime(audioTime + this.timebase.outputLatencySeconds)
      - this.transport.getOrigin()
      + this.getDeltaTime()
      - this.playoutDelaySeconds;

    // Même échelle que `tickIn` : la vitesse presse les secondes avant la
    // conversion. Sans quoi la fenêtre d'ordonnancement irait chercher les
    // notes ailleurs qu'où la tête les dessine, dès qu'un tempo change.
    return this.transport.timeline.secondsToTick(seconds * this.playbackRate) ?? 0;
  }

  on(callback: (event: LocalTransportEvent) => void) {
    this.eventListeners.add(callback);

    return () => {
 this.eventListeners.delete(callback);
};
  }

  private emit(event: LocalTransportEvent) {
    this.eventListeners.forEach(listener => listener(event));
  }

  private getDeltaTime(at?: number) {
    return this.deltaTime - (this.detachedAt !== undefined ? this.elapsedAt(at) - this.detachedAt : 0);
  }

  destroy(): void {
    this.ticker?.unregister(this.boundOnSchedule);
    this.listeners.clear();
    this.eventListeners.clear();
  }
}
